import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
  Avatar,
  Button,
  TextField,
  Box,
  Typography,
  Container,
  Alert,
  Grid,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, recovery } = useContext(AuthContext);
  const navigate = useNavigate();

  const [openRecovery, setOpenRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryError, setRecoveryError] = useState('');

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleOpenRecoveryDialog = (event) => {
    event.preventDefault();
    setOpenRecovery(true);
    // Réinitialiser les champs lors de l'ouverture
    setRecoveryEmail('');
    setRecoveryMessage('');
    setRecoveryError('');
  };

  const handleCloseRecoveryDialog = () => {
    setOpenRecovery(false);
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) {
      setRecoveryError('Veuillez remplir tous les champs.');
      return;
    }
    setRecoveryMessage('');
    try {
      await recovery(recoveryEmail);
      setRecoveryMessage('Si un compte est associé à cet e-mail, un lien de récupération a été envoyé.');
      setRecoveryError('');
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'USER_NOT_FOUND') {
        // Pour des raisons de sécurité, on affiche le même message de succès
        setRecoveryMessage('Si un compte est associé à cet e-mail, un lien de récupération a été envoyé.');
      } else {
        setRecoveryError('Une erreur est survenue. Veuillez réessayer.');
      }
      setRecoveryMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    setError('');
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const errorCode = err.response?.data?.error;
      switch (errorCode) {
        case 'LOGIN_ERROR':
          setError('Erreur lors de la connexion.');
          break;
        case 'INVALID_CREDITENTIALS':
          setError('Email ou mot de passe invalide.');
          break;
        default:
          setError('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Login
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Adresse Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="start"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Se connecter
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid>
              <Link href="#" variant="body2" onClick={handleOpenRecoveryDialog}>
                Mot de passe oublié ?
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Dialog open={openRecovery} onClose={handleCloseRecoveryDialog}>
        <DialogTitle>Récupération de mot de passe</DialogTitle>
        <DialogContent>
          {recoveryMessage && <Alert severity="success" sx={{ mb: 2 }}>{recoveryMessage}</Alert>}
          {recoveryError && <Alert severity="error" sx={{ mb: 2 }}>{recoveryError}</Alert>}

          {!recoveryMessage && (
            <>
              <DialogContentText>
                Pour réinitialiser votre mot de passe, veuillez saisir votre adresse e-mail. Nous vous enverrons les instructions.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="recovery-email"
                label="Adresse Email"
                type="email"
                fullWidth
                variant="standard"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRecoveryDialog}>
            {recoveryMessage ? 'Fermer' : 'Annuler'}
          </Button>
          {!recoveryMessage && (
            <Button onClick={handleRecoverySubmit}>
              Envoyer
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;